import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { Footer } from '../../../components/common/Footer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Loading } from '../../../components';
import { useQuery } from '@apollo/client';
import { FETCH_MATRIX } from '../queries';
import { DependencyMatrix, Products } from './DependencyMatrix';
import { LOCATION_TYPES } from '../../../constants';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import { Tag } from 'antd';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface TableData {
  location: string;
  percentage: number;
  supportedRevenue: number;
  locationId: string;
  locationType: string;
}

const convertData = (data: Products): any[] => {
  const dataToSort = [...(data?.locationAuxs ?? [])].toSorted((a, b) =>
    a.name.localeCompare(b.name),
  );

  //sort by distribution center
  const orderedData = dataToSort.toSorted((a, b) => {
    if (a.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER) return 1;
    if (b.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER) return -1;
    return 0;
  });

  return orderedData?.map((location) => {
    return {
      location: location.name,
      percentage: location.percentage,
      supportedRevenue: location.supportedRevenue,
      locationId: location.key,
      locationType: location.locationType,
    };
  });
};

function FinishedGoodsSummary() {
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionId = params.divisionId;
  const level = parseInt(searchParams.get('level')!);
  const divisionName = searchParams.get('divisionName') || '';
  const { data, loading, error } = useQuery<DependencyMatrix>(FETCH_MATRIX, {
    variables: {
      division: { uuid: divisionId },
      level: level,
    },
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => tableSortingMethods.string(a.location, b.location),
      render: (text: string, record) => (
        <div className="flex flex-col">
          <span className="custom-table--column">{text}</span>
          <div>
            {record.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER && (
              <div className="ml-3">
                <Tag color="green">Distribution Center</Tag>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '% Of Division Supported',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.percentage, b.percentage),
      render: (text: string) => (
        <span className="custom-table--column">{text} %</span>
      ),
    },
    {
      title: 'Division Revenue Supported',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.supportedRevenue, b.supportedRevenue),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) =>
        record?.locationType !== LOCATION_TYPES.DISTRIBUTION_CENTER && (
          <Button
            type="primary-rounded"
            className="text-sm"
            onClick={() =>
              navigate(
                `/dependency-mapping/${divisionId}/${
                  record.locationId
                }/level-locations?locationId=${record.locationId}&level=${
                  level + 1
                }&divisionName=${divisionName}&locationName=${record.location}`,
              )
            }
          >
            Map Level 1
          </Button>
        ),
    },
  ];

  const baseColumns: ExportColumn[] = [
    { key: 'location', header: 'Location', type: 'text', width: 45 },
    {
      key: 'percentage',
      header: '% Of Division Supported',
      type: 'percentage',
      width: 25,
    },
    {
      key: 'supportedRevenue',
      header: 'Division Revenue Supported',
      type: 'money',
      width: 30,
    },
  ];

  return (
    <>
      <TitleUpdater title="Finished Goods Summary" />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Finished Goods Summary // ${divisionName}`}
        brief="Beyond finished goods locations, it is critical to capture more of the organization’s value
                       chain. Doing so highlights locations that support large parts of the company's revenue, despite
                       not necessarily selling any finished goods to customers. Level 1 dependency mapping is focused on locations that directly support the finished goods locations defined previously. Deeper
                       dependencies will be mapped later. Click on any of the finished goods locations to capture the
                       level 1 dependencies. If a location is missing here, go back to the finished goods mapping and
                       add it there."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Finished Goods Summary"
          columns={columns}
          data={
            data?.findDivisionDependencyMatrix?.division
              ? convertData(data.findDivisionDependencyMatrix.division)
              : []
          }
          loading={loading}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/dependency-mapping/division-selection/${divisionId}/finished-goods-locations/finished-goods-mapping?level=0&divisionName=${divisionName}`}
      />
    </>
  );
}

export default FinishedGoodsSummary;
